'use client'

import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { usePathname } from 'next/navigation'

const BannerLandingPage: React.FC<{ url: string }> = ({ url }) => {
  const pathname = usePathname()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false)

  const isHome = pathname === '/home'
  const additionalStyles = isHome
    ? 'h-[200px] sm:h-[360px] rounded-[30px] mx-0 sm:mx-[16px]'
    : 'h-[600px]'
  const gradient = isHome
    ? 'rounded-[30px] bg-[linear-gradient(0deg,rgba(13,120,110,0.3),rgba(13,120,110,0.3)),linear-gradient(102.16deg,rgba(0,0,0,0.8)-232.34%,rgba(255,255,255,0.8)257.27%)]'
    : 'bg-gradient-radial-landing'

  const videoCss = isHome ? 'rounded-[30px]' : ''

  useEffect(() => {
    if (typeof window === 'undefined') return

    const mediaQuery = window.matchMedia('(max-width: 639px)')
    const updateIsMobile = (event?: MediaQueryListEvent) => {
      setIsMobile(event ? event.matches : mediaQuery.matches)
    }

    updateIsMobile()
    mediaQuery.addEventListener('change', updateIsMobile)

    return () => mediaQuery.removeEventListener('change', updateIsMobile)
  }, [])

  useEffect(() => {
    if (!isHome) {
      setIsVisible(true)
      return
    }

    const node = containerRef.current
    if (!node || typeof window === 'undefined') return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.25 }
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [isHome])

  useEffect(() => {
    if (!isVisible) return

    if (isMobile) {
      setShouldLoadVideo(false)
      return
    }

    if (!isHome) {
      setShouldLoadVideo(true)
      return
    }

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(() => {
        setShouldLoadVideo(true)
      }, { timeout: 2500 })

      return () => window.cancelIdleCallback(idleId)
    }

    const timeoutId = setTimeout(() => setShouldLoadVideo(true), 1200)
    return () => clearTimeout(timeoutId)
  }, [isHome, isMobile, isVisible])

  useEffect(() => {
    if (!shouldLoadVideo || !videoRef.current) return

    videoRef.current.play().catch(() => {
      // Ignore autoplay promise rejections on restrictive browsers.
    })
  }, [shouldLoadVideo])

  return (
    <div ref={containerRef} className={`${additionalStyles} relative w-full`}>
      {isMobile ? (
        <Image
          src="/home/bg-video-image.png"
          alt=""
          fill
          priority={isHome}
          sizes="100vw"
          className={`absolute inset-0 h-full w-full object-cover ${videoCss}`}
        />
      ) : (
        <video
          preload={isHome ? 'none' : 'metadata'}
          ref={videoRef}
          className={`absolute inset-0 h-full w-full object-cover ${videoCss}`}
          autoPlay={shouldLoadVideo}
          loop
          playsInline
          muted
          src={shouldLoadVideo ? url : undefined}
        />
      )}
      <div className={`${gradient} absolute inset-0 z-10`}></div>
    </div>
  )
}
export default BannerLandingPage
